import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, throwError } from 'rxjs';
import User from '../../models/user.model';
import Authorization from '../../models/authorization.model';
import ApiResponse from '../../models/common/api-response.model';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import ErrorResponse from '../../models/common/error-response.model';


export interface LoginResponse {
  user: User;
  authorization: Authorization;
}

export interface RegisterRequest {
  user: User;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl: string = environment.BASE_URL;
  currentUser$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null)

  constructor(private httpClient: HttpClient) { }

  /**======== SERVICE =============*/

  login(
    email: string,
    password: string
  ): Observable<ApiResponse<LoginResponse | ErrorResponse>> {
    return this.httpClient
      .post<ApiResponse<LoginResponse>>(`${this.baseUrl}/api/users/login`, {
        emailAddress: email,
        password: password,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401)
            return of(error.error);

          return throwError(error);
        })
      );
  }

  register(user: User, password: string): Observable<ApiResponse<User | ErrorResponse>> {
    return this.httpClient.post<ApiResponse<User>>(
      `${this.baseUrl}/api/users/register`,
      {
        user: user,
        password: password,
      }
    );
  }

  updatePassword(currentPassword: string, newPassword: string): Observable<ApiResponse<null | ErrorResponse>> {
    return this.httpClient.post<ApiResponse<ErrorResponse>>(
      `${this.baseUrl}/api/users/update-user-password`,
      {
        currentPassword,
        newPassword
      }, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.getAuthenticationToken()}`
      })
    });
  }

  updateIban(iban: string): Observable<ApiResponse<null | ErrorResponse>> {
    return this.httpClient.post<ApiResponse<ErrorResponse>>(
      `${this.baseUrl}/api/users/update-user-iban`,
      { iban },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.getAuthenticationToken()}`
        })
      });
  }

  updateWallet(newIncomingWalletHash: string, newOutcomingWalletHash: string): Observable<ApiResponse<null | ErrorResponse>> {
    return this.httpClient.post<ApiResponse<ErrorResponse>>(
      `${this.baseUrl}/api/users/update-user-wallet`,
      { newIncomingWalletHash, newOutcomingWalletHash },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.getAuthenticationToken()}`
        })
      });
  }

  updateEmailAddress(emailAddress: string): Observable<ApiResponse<null | ErrorResponse>> {
    return this.httpClient.post<ApiResponse<ErrorResponse>>(
      `${this.baseUrl}/api/users/start-update-user-email`,
      { emailAddress },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.getAuthenticationToken()}`
        })
      });
  }

  startRestorePassword(emailAddress: string): Observable<ApiResponse<null | ErrorResponse>> {
    return this.httpClient.post<ApiResponse<null | ErrorResponse>>(
      `${this.baseUrl}/api/users/start-restore-password`,
      { emailAddress }
    );
  }

  completeRestorePassword(emailAddress: string, token: string, password: string): Observable<ApiResponse<null | ErrorResponse>> {
    return this.httpClient.post<ApiResponse<null | ErrorResponse>>(
      `${this.baseUrl}/api/users/complete-restore-password`,
      {
        emailAddress: emailAddress,
        token: token,
        password: password
      }
    );
  }

  /** ==== ADMIN FUNCTION ====
  getUsers(): Observable<ApiResponse<User>> {
    return this.httpClient.get<ApiResponse<User>>(
      `${this.baseUrl}/api/users`, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.getAuthenticationToken()}`
      })
    }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 409)
          return of(error.error);

        return throwError(error);
      })
    );
  }
  **/
  getUserById(id: string): Observable<ApiResponse<User>> {
    return this.httpClient.get<ApiResponse<User>>(
      `${this.baseUrl}/api/users/${id}`, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.getAuthenticationToken()}`
      })
    }
    )
  }


  /**======== UTILS =============*/

  setCurrentUserId(id: string): void {
    localStorage.setItem('currentUserId', id);
  }

  getCurrentUserId(): string | null {
    return localStorage.getItem('currentUserId');
  }

  setAuthenticationToken(token: string) {
    localStorage.setItem('token', token);
  }

  getAuthenticationToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    const currentUser = this.getCurrentUserId();
    const token = this.getAuthenticationToken();

    return (
      currentUser !== null &&
      currentUser !== undefined &&
      token !== null &&
      token !== undefined
    );
  }

  clearAuth(): void {
    localStorage.clear();
    window.location.href = '/';
  }

}
